import { Component, inject, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { Role } from '@adgate/modules/admin/types/role.type';
import { AdminService } from '@adgate/modules/admin/services/admin-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { SelectionModel } from '@angular/cdk/collections';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomValidators } from '@adgate/common/validators/custom-validators.validator';
import { Privilege } from '@adgate/modules/admin/types/privilege.type';

@Component({
  selector: 'app-role-edit-dialog-component',
  imports: [
    CommonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    ReactiveFormsModule
  ],
  templateUrl: './role-edit-dialog-component.html',
  styleUrl: './role-edit-dialog-component.scss',
})
export class RoleEditDialogComponent implements OnInit, AfterViewInit
{
  private adminService: AdminService = inject(AdminService);
  private dialogService: DialogService = inject(DialogService);
  private dialogRef: MatDialogRef<RoleEditDialogComponent> = inject(MatDialogRef);
  private fb: FormBuilder = inject(FormBuilder);

  public data: Role = inject(MAT_DIALOG_DATA, { optional: false });

  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = ['select', 'privilege'];
  dataSource: MatTableDataSource<Privilege> = new MatTableDataSource<Privilege>();
  privilegeSelection = new SelectionModel<Privilege>(true, []);
  roleForm: FormGroup;

  constructor()
  {
    this.roleForm = this.fb.group({
      roleName: ['', [Validators.required, CustomValidators.invalidCharactersValidator()]],
      showToOfficeAdmins: [false]
    });
  }

  ngOnInit(): void
  {
    this.dialogService.showProgress();
    this.adminService.getPrivileges().subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dataSource.data = r.data;

        if( this.data?.id != -1 ) {
          this.roleForm.get('roleName')?.setValue(this.data?.description);
          this.roleForm.get('showToOfficeAdmins')?.setValue(this.data?.showToOfficeAdmins);
          this.data?.privileges.forEach((p: Privilege) => {
            this.dataSource.data.forEach(row => {
              if( p != null && p.id == row.id ) {
                this.privilegeSelection.select(row);
              }
            });
          });
        }

        this.dialogService.hideProgress();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = (item: Privilege, property: string) => {
      switch(property) {
        case 'privilege': return item.description!.toLowerCase();
        default: return (item as any)[property];
      }
    };

    this.dataSource.sort = this.sort;

    this.sort.active = 'privilege';
    this.sort.direction = 'asc';
    this.dataSource.sort = this.sort;
  }

  onCancel(): void 
  {
    this.dialogRef.close(null);
  }

  onSubmit(): void 
  {
    if( this.roleForm.valid ) {
      this.data.description = this.roleForm.get('roleName')?.value;
      this.data.showToOfficeAdmins = this.roleForm.get('showToOfficeAdmins')?.value;
      this.data.privileges = this.privilegeSelection.selected;

      this.dialogRef.close( this.data );
    }
  }

  masterToggle() 
  {
    this.isAllSelected() ?
    this.privilegeSelection.clear() :
    this.dataSource.data.forEach(row => this.privilegeSelection.select(row));
  }

  isAllSelected() 
  {
    const numSelected = this.privilegeSelection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }
}
